import { trim } from "lodash-es"
import { useCallback, useState } from "react"

import { useCheckLogin, useJoinedTeam, useOwnTeam } from "@/hooks"
import { i18n } from "@/i18n"
import { msg } from "@/topics/Recall/const/msg"
import { joinTeam, searchTeam } from "@/services/core"
import { collectRecallInviteSuccess } from "@/utils/event"
import { showConfirm } from "../Confirm"
import { Modal } from "../Modal"
import StrokeText from "../StrokeText"
import Toast from "../Toast"
import CodeInput from "./CodeInput"

import styles from "./index.module.scss"

export const useInviteSubmit = (onSuccess?: () => void) => {
    const checkLogin = useCheckLogin()
    const { data: ownTeam } = useOwnTeam()

    return async (inviteCode: string) => {
        if (!checkLogin()) return
        const code = trim(inviteCode)
        if (!code) {
            Toast.message(i18n.getRecall("message.code_empty"))
            return
        }

        const searchRes = await searchTeam(code)
        if (searchRes.code !== 0) {
            Toast.message(i18n.getRecall("message.search_no_team"))
            return
        }

        const searchData = searchRes.data
        // 防止加入自己的队伍
        if (searchData.code === ownTeam?.code) {
            Toast.message(i18n.getRecall("message.join_myself"))
            return
        }

        const { captain } = searchData
        showConfirm({
            content: (
                <div
                    className={styles.confirmTo}
                    dangerouslySetInnerHTML={{
                        __html: i18n.getRecall("record.combine_confirmJoin", {
                            nickname: captain.nickname || "unknown",
                        }),
                    }}
                />
            ),
            onOk: async () => {
                const removeParam = () => {
                    const url = new URL(window.location.href)
                    url.searchParams.delete("invite_code")
                    window.history.replaceState({}, document.title, url.toString())
                }

                const joinRes = await joinTeam(code)
                if (joinRes.code === 0) {
                    collectRecallInviteSuccess(code, captain.uid)
                    removeParam()
                    Toast.message(i18n.getRecall("message.join_success"))
                    onSuccess?.()
                } else {
                    Toast.message(`${i18n.getRecall(msg?.[joinRes.code]) || joinRes.msg}`)
                }
            },
        })
    }
}

interface Props {
    open: boolean
    onCancel: () => void
}

const InviteCodeModal: React.FC<Props> = (props) => {
    const { open, onCancel } = props

    const [inviteCode, setInviteCode] = useState(
        () => new URLSearchParams(window.location.search).get("invite_code") ?? "",
    )

    const joinedTeam = useJoinedTeam()
    const handleSuccess = useCallback(() => {
        joinedTeam?.mutate()
    }, [joinedTeam])

    const submitInvite = useInviteSubmit(handleSuccess)
    const handleOk = useCallback(() => {
        submitInvite(inviteCode)
        onCancel()
    }, [inviteCode, submitInvite, onCancel])

    return (
        <Modal
            open={open}
            onCancel={onCancel}
            showCancelBtn={false}
            onOk={handleOk}
            contentClassName={styles.inviteCode}
            actionsClassName={styles.actions}
        >
            <div className={styles.title}>
                <StrokeText
                    text={i18n.getRecall("invite.title")}
                    fontFamily="source-han-sans-japanese"
                    fontWeight={700}
                    fontSize={32}
                    fillColor="#ffffff"
                    borderWidth={3}
                    borderColor="#614518"
                    offsetY={0}
                    padding={3}
                    baselineAligned
                    className={styles.text}
                />
            </div>
            <CodeInput
                value={inviteCode}
                onChange={(val) => setInviteCode(val)}
            />
        </Modal>
    )
}

export default InviteCodeModal
